// ============================================================================
// /bids — every bid, newest first. Launch day reads "No bids yet. Start one
// from a walkthrough." (the prompt's words), never an empty table.
// ============================================================================

import { Link } from 'react-router-dom';
import { Button, EmptyState, ErrorState, PageHeader, Skeleton } from '../../shared/components';
import { fetchBids, fetchPriceLines, STATUS_WORDS, typeLabel, type BidRow } from './api';
import { money, shortDate } from './format';
import { useBidLoad } from './use-bid-load';

export default function BidsList() {
  // the monthly price is nightly cleaning plus what step 3 adds, from the database (#841)
  const { data, loading, error, refetch } = useBidLoad<{ bids: BidRow[]; total: Map<string, number | null> }>(
    async () => {
      const bids = await fetchBids();
      const lines = await fetchPriceLines(bids.map((b) => b.bid_id));
      return { bids, total: new Map(lines.map((l) => [l.bid_id, l.total_monthly])) };
    },
    [],
    { bids: [], total: new Map() },
  );
  const bids = data.bids;

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader
        eyebrow="Sales"
        title="Bids"
        meta="From the walkthrough to an executive's approval — priced off the book, checked with an account manager."
        actions={
          <>
            <Button variant="secondary" size="sm" as={Link} to="/bids/settings">Pricing settings</Button>
            <Button size="sm" as={Link} to="/bids/new">Start a bid</Button>
          </>
        }
      />
      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : error ? (
        <ErrorState body={error} onRetry={refetch} />
      ) : bids.length === 0 ? (
        <EmptyState
          title="No bids yet. Start one from a walkthrough."
          body="Documents and sending are coming with the proposal step."
          cta={<Button as={Link} to="/bids/new">Start a bid</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-tcs-border bg-tcs-bg">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-tcs-text-muted">
              <tr className="border-b border-tcs-border">
                <th className="px-3 py-2">Prospect</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Market</th>
                <th className="px-3 py-2">Where it is</th>
                <th className="px-3 py-2 text-right">Monthly</th>
                <th className="px-3 py-2">Checked with an AM</th>
                <th className="px-3 py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {bids.map((b) => (
                <tr key={b.bid_id} className="border-b border-tcs-border last:border-0 hover:bg-tcs-surface">
                  <td className="px-3 py-2">
                    <Link className="font-medium text-tcs-text hover:underline" to={`/bids/${b.bid_id}`}>{b.facility_name}</Link>
                    <div className="text-xs text-tcs-text-muted">{[b.prospect_city, b.prospect_state].filter(Boolean).join(', ') || '—'}</div>
                  </td>
                  <td className="px-3 py-2">{typeLabel(b.facility_type)}</td>
                  <td className="px-3 py-2">{b.market_region ?? '—'}</td>
                  <td className="px-3 py-2">{STATUS_WORDS[b.status] ?? b.status}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(data.total.get(b.bid_id) ?? b.quoted_monthly)}</td>
                  <td className="px-3 py-2">{b.am_reviewed_by_eid ? `Yes · ${shortDate(b.am_reviewed_at)}` : 'Not yet'}</td>
                  <td className="px-3 py-2 text-xs text-tcs-text-muted">{shortDate(b.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
